import { Check, MapPin, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Eyebrow } from "@/components/PageHero";
import { QuoteButton } from "@/components/QuoteButton";
import { MenuButton } from "@/components/MenuButton";
import { MenusSection } from "@/components/MenusSection";
import { HalalSection } from "@/components/HalalSection";
import { useContent, useSiteImage } from "@/context/ContentContext";
import { useReveal, Seo } from "@/lib/seo";

const PRESTATIONS = [
  "Mariages et cérémonies",
  "Anniversaires et fêtes privées",
  "Brunchs et réceptions",
  "Événements d'entreprise et institutionnels",
  "Cocktails et vins d'honneur",
  "Inaugurations, conférences et séminaires",
  "Associations et clubs",
  "Catering pour tournages, productions, spectacles et artistes",
  "Prestations culinaires pour équipes et événements spécifiques",
];

const FORMATS = [
  "Cocktail, vin d'honneur et apéritifs uniquement",
  "Repas uniquement",
  "Cocktail ou vin d'honneur suivi d'un repas",
  "Buffet en libre-service",
  "Buffet disposé sur les tables",
  "Service à l'assiette",
  "Formule personnalisée selon le projet",
];

const CUISINES = [
  "Cuisine africaine halal",
  "Cuisine antillaise et des îles halal",
  "Cuisine française halal",
  "Cuisine afro-caribéenne halal",
  "Menus personnalisés selon les besoins et les possibilités",
];

export default function Traiteur() {
  useReveal();
  const { content } = useContent();
  const img = useSiteImage();
  const zone = content?.zones?.traiteur;

  return (
    <div data-testid="traiteur-page">
      <Seo
        title="Traiteur halal africain, antillais, français & afro-caribéen — Afro Regal"
        description="Traiteur halal pour mariages, cocktails, vins d'honneur, entreprises et catering : traiteur africain halal, traiteur antillais halal, traiteur français halal et afro-caribéen halal. Option non halal sur demande. Devis dans l'Oise, l'Île-de-France et les Hauts-de-France."
      />
      <section className="bg-white pb-14 pt-32 lg:pt-40" data-testid="traiteur-hero">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid items-end gap-8 lg:grid-cols-12">
            <div className="fade-up lg:col-span-8">
              <p className="mb-6 flex items-center gap-3 text-sm uppercase tracking-[0.3em] text-[#9A7B2C]"><span className="h-px w-10 bg-[#C9A84C]" /> Traiteur événementiel</p>
              <h1 className="font-serif text-5xl font-500 leading-[0.95] text-[#2C2C2C] sm:text-6xl lg:text-8xl">Une cuisine d'exception pour <em className="text-[#C9A84C]">chaque</em> événement</h1>
            </div>
            <div className="fade-up lg:col-span-4">
              <p className="text-base leading-relaxed text-[#2C2C2C]/70">Mariages, réceptions, cocktails, événements d'entreprise ou catering de production : nous composons une prestation sur mesure, à votre image — en cuisine <strong className="text-[#2C2C2C]">africaine, antillaise, française ou afro-caribéenne halal</strong>.</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <QuoteButton testId="traiteur-hero-quote" service="Traiteur uniquement" />
                <MenuButton variant="outline" testId="traiteur-hero-carte">Découvrir notre carte</MenuButton>
              </div>
            </div>
          </div>
          <div className="mt-14 overflow-hidden rounded-[999px] lg:mt-20">
            <img src={img("traiteur_hero")} alt="Buffet traiteur élégant" className="h-56 w-full object-cover sm:h-72 lg:h-96" />
          </div>
        </div>
      </section>

      {/* Prestations */}
      <section id="mariages" className="bg-white py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="reveal mb-12 max-w-2xl">
            <Eyebrow>Nos prestations</Eyebrow>
            <h2 className="font-serif text-3xl font-500 text-[#2C2C2C] lg:text-4xl">Des prestations pour tous vos projets</h2>
          </div>
          <div className="grid border-t border-[#2C2C2C]/15 sm:grid-cols-2 sm:gap-x-10 lg:grid-cols-3">
            {PRESTATIONS.map((p, i) => (
              <div key={p} className="reveal flex items-baseline gap-5 border-b border-[#2C2C2C]/15 py-6" style={{ transitionDelay: `${i * 40}ms` }} data-testid={`prestation-${i}`}>
                <span className="font-serif text-3xl text-[#C9A84C]">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-base text-[#2C2C2C]/85">{p}</span>
              </div>
            ))}
          </div>
          <div className="reveal mt-10 flex flex-wrap items-center gap-4">
            <MenuButton variant="primary" testId="traiteur-mariages-carte">Découvrir notre carte</MenuButton>
            <Link to="/mariages" data-testid="traiteur-mariages-link" className="inline-flex items-center gap-2 rounded-full border border-[#2C2C2C] px-7 py-3 text-sm font-medium text-[#2C2C2C] transition-colors hover:bg-[#2C2C2C] hover:text-white">Vous préparez un mariage ? <ArrowRight size={15} /></Link>
          </div>
        </div>
      </section>

      {/* Formats + Cuisines */}
      <section id="prives" className="bg-[#FAF6EF] py-24 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-6 px-5 lg:grid-cols-2 lg:px-8">
          <div className="reveal rounded-[28px] bg-white p-8 lg:p-12">
            <Eyebrow>Formats possibles</Eyebrow>
            <h2 className="font-serif text-3xl font-500 text-[#2C2C2C]">Le format qui vous ressemble</h2>
            <ul className="mt-7 space-y-3">
              {FORMATS.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-[#2C2C2C]/80">
                  <Check size={16} className="mt-0.5 shrink-0 text-[#C9A84C]" /> {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="reveal rounded-[28px] bg-[#2C2C2C] p-8 text-white lg:p-12">
            <Eyebrow>Cuisines</Eyebrow>
            <h2 className="font-serif text-3xl font-500 text-white">Des saveurs d'ici et d'ailleurs</h2>
            <ul className="mt-7 space-y-3">
              {CUISINES.map((c) => (
                <li key={c} className="flex items-start gap-3 text-sm text-white/80">
                  <Check size={16} className="mt-0.5 shrink-0 text-[#C9A84C]" /> {c}
                </li>
              ))}
            </ul>
            <p className="mt-7 rounded-xl border border-[#C9A84C]/40 p-4 text-sm text-white/80">
              Le <strong>halal</strong> est notre offre principale : chaque carte est pensée en halal. Une option non halal reste possible sur demande. Chaque carte est étudiée selon vos besoins et nos possibilités.
            </p>
          </div>
        </div>
      </section>

      {/* Menus */}
      <HalalSection variant="light" title="Traiteur africain, antillais, français et afro-caribéen halal" testId="traiteur-halal" />
      <MenusSection />

      {/* Entreprises / Catering anchors + no price note */}
      <section id="entreprises" className="py-24 lg:py-28">
        <div className="mx-auto max-w-4xl px-5 text-center lg:px-8">
          <div id="catering" className="reveal">
            <Eyebrow>Sur devis</Eyebrow>
            <h2 className="font-serif text-3xl font-500 text-[#2C2C2C] lg:text-4xl">Toutes nos prestations sont sur devis</h2>
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-[#2C2C2C]/70">
              Nous construisons chaque proposition en fonction de votre événement, de votre nombre de convives et de vos envies. Aucun tarif figé : une étude personnalisée pour un résultat à la hauteur de vos attentes.
            </p>
          </div>
          <div className="reveal mt-10 inline-flex items-start gap-3 rounded-xl border border-[#2C2C2C]/10 bg-white p-5 text-left">
            <MapPin size={20} className="mt-0.5 shrink-0 text-[#C9A84C]" />
            <p className="text-sm text-[#2C2C2C]/80">{zone}</p>
          </div>
          <div className="reveal mt-10 flex justify-center">
            <QuoteButton testId="traiteur-cta-quote" service="Traiteur uniquement" />
          </div>
        </div>
      </section>
    </div>
  );
}
